import { CardTypeEnum, ExtendedReferencedUser } from "@finway-group/shared/lib/models"
import { Alert, Button, Card } from "antd"
import CreditCard from "Components/creditCard/creditCard"
import Loading from "Components/loading"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import CardService from "Shared/services/card.service"
import NotificationService, { NotificationTypeEnum } from "Shared/services/notification.service"
import { syncCreditCard } from "Shared/store/actions/creditCard/creditCardActions"
import { isNotSet } from "Shared/utils/helper.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface CreditCardSectionProps {
    cardId: string
    card: any
    isCardLoading: boolean
    requestedBy: ExtendedReferencedUser
    currency: string
    infoAlertMessage?: string
}

export const CreditCardSection = ({ cardId, card, isCardLoading, requestedBy, currency, infoAlertMessage }: CreditCardSectionProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [isRetrying, setIsRetrying] = useStateIfMounted<boolean>(false)
    const hasCardCreated = !isNotSet(cardId)
    const hasCardError = !isNotSet(card?.errorCode)

    const createDefaultCardDisplay = () => ({
        user: requestedBy,
        cardType: CardTypeEnum.VIRTUAL,
        nameOnCard: `${requestedBy.firstName} ${requestedBy.lastName}`,
    })

    const onRetryCardCreation = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        setIsRetrying(true)
        CardService.fixCardError(card._id)
            .then((fixedCard) => {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:cards.fix_card.title"), t("notification:cards.fix_card.message"))
                dispatch(syncCreditCard(fixedCard))
            })
            .catch((err: unknown) => {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
            })
            .finally(() => setIsRetrying(false))
    }

    return (
        <Card className="text-center mb-20">
            <CreditCard
                isCardLoading={isCardLoading}
                card={card || createDefaultCardDisplay()}
                showActionBtn={hasCardCreated && !hasCardError}
                showBudget={hasCardCreated && !hasCardError}
                dimension="sm:w-400 sm:h-252 lg:w-280 lg:h-176 md:w-300 md:h-189 xl:w-400 xl:h-252"
                showPaddingInfo={currency !== card?.currency}
            />
            {!hasCardCreated && infoAlertMessage ? (
                <Alert type="info" showIcon className="mt-20" message={infoAlertMessage} />
            ) : (
                hasCardError &&
                (isRetrying ? (
                    <Loading className="mt-4 h-60" />
                ) : (
                    <Alert
                        type="error"
                        showIcon
                        className="mt-20"
                        message={
                            <>
                                <span>{t("error:cards.something_went_wrong")}</span>
                                <Button className="button-warning-notification ml-4" type="text" onClick={onRetryCardCreation}>
                                    {t("action:cards.or_retry")}
                                </Button>
                            </>
                        }
                    />
                ))
            )}
        </Card>
    )
}
